import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../components';
import { Button, Alert } from '../../components/primitives';
import { CodeInput } from '../../components/CodeInput';
import { ResultCard } from '../../components/ResultCard';
import { AuthLayout } from '../AuthLayout';

/** Seconds before the code can be sent again, so the button isn't hammered. */
const RESEND_COOLDOWN = 45;

/** Seconds on the verified card before it takes them to sign-in by itself. */
const REDIRECT_AFTER = 5;

/** "0:42" — the form the design states the wait in. */
const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export default function ConfirmEmail() {
  const navigate = useNavigate();
  const auth = useAuth();
  const notification = useNotification();

  interface ConfirmEmailValues {
    code: string;
    email: string;
  }
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';

  const [success, setSuccess] = useState<boolean>(false);
  const [cooldown, setCooldown] = useState<number>(0);
  const [redirectIn, setRedirectIn] = useState<number>(REDIRECT_AFTER);

  const goToSignIn = useCallback(() => {
    navigate('/login?email=' + encodeURIComponent(email));
  }, [navigate, email]);

  // Deferred navigation lives in an effect so it is cleanup-safe and survives
  // provider re-renders. It counts down a second at a time rather than waiting
  // in one lump, so the card can say how long is left and the person can skip
  // it instead of watching a screen that looks stuck.
  useEffect(() => {
    if (!success) return undefined;
    if (redirectIn <= 0) {
      goToSignIn();
      return undefined;
    }
    const timer = setTimeout(() => setRedirectIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [success, redirectIn, goToSignIn]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const confirmEmailValidation = Yup.object().shape({
    code: Yup.string().required(),
  });

  const handleReSendCode = () => {
    setCooldown(RESEND_COOLDOWN);
    auth &&
      auth
        .resendCode(email)
        .then(() => {
          notification &&
            notification.success?.({
              message: 'Verification Code Re-sent',
              description: 'Check your email for a new verification code. ',
              duration: 5,
            });
        })
        .catch((err) => {
          console.log(err);
          setCooldown(0);
          notification &&
            notification.error?.({
              message: 'Verification Code Error',
              description: 'Could not re-send verification code. ',
              duration: 5,
            });
        });
  };
  const handleOnSubmit = useCallback(
    (
      values: ConfirmEmailValues,
      actions: FormikHelpers<ConfirmEmailValues>
    ) => {
      setSubmitError(undefined);
      auth &&
        auth
          .verifyCode({ username: email, code: values.code })
          .then(() => {
            actions.setSubmitting(false);
            setSuccess(true);
          })
          .catch((err) => {
            actions.setSubmitting(false);
            setSubmitError(err.message);
          });
    },
    [auth, email, navigate]
  );

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
        <ResultCard
          tone="success"
          icon={CheckCircle2}
          title="Email verified"
          description="Your account is ready to sign in."
          action={{ label: 'Continue to sign in', onAction: goToSignIn }}
          footer={`Taking you there in ${redirectIn}s — or click now.`}
          testId="email-verified"
        />
      </div>
    );
  }

  return (
    <AuthLayout
      title="Check your email"
      subtitle={
        email ? (
          <>
            We sent a 6-digit code to <b className="text-foreground">{email}</b>
            {' · '}
            {/*
              A mistyped address is the commonest reason the code never
              arrives, and the only way out of it was the browser's back
              button. Sign-up is where the address is entered, so that is
              where this goes, carrying the current one to correct.
            */}
            <Link
              to={`/signup?email=${encodeURIComponent(email)}`}
              className="text-primary underline-offset-2 hover:underline"
            >
              change
            </Link>
          </>
        ) : (
          'Enter the 6-digit code sent to your email inbox.'
        )
      }
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Formik
        initialValues={{
          code: '',
          email: email || '',
        }}
        onSubmit={handleOnSubmit}
        validationSchema={confirmEmailValidation}
      >
        {(props: FormikProps<ConfirmEmailValues>) => (
          <Form className="space-y-5">
            {/*
              Six boxes rather than one long field: the expected length is
              visible, and the transposition mistakes a single field invites
              stop happening. The whole value is still submitted as one
              `code` field.
            */}
            <CodeInput
              name="code"
              value={props.values.code}
              onChange={(value) => {
                setSubmitError(undefined);
                props.setFieldValue('code', value);
              }}
              // Submit only once Formik has the last digit: setFieldValue is
              // asynchronous, so submitting in the same tick would send the
              // code minus its final character.
              onComplete={(value) => {
                void props.setFieldValue('code', value).then(() => props.submitForm());
              }}
              disabled={props.isSubmitting}
              error={props.touched.code && props.errors.code ? props.errors.code : undefined}
              autoFocus
            />
            <Button type="submit" className="w-full" disabled={props.isSubmitting}>
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Verify
            </Button>
            {/* One sentence while the clock runs — "Paste works. Resend
                available in 0:42." is a single statement about this form, and
                splitting it across nodes made it two half-sentences that only
                looked like one. Once the wait is over the second half becomes
                a control, and then it has to be its own element. */}
            <p className="text-center text-xs text-muted-foreground">
              {cooldown > 0 ? (
                `Paste works. Resend available in ${clock(cooldown)}.`
              ) : (
                <>
                  {'Paste works. '}
                  <Button type="button" variant="link" className="h-auto p-0" onClick={handleReSendCode}>
                    Resend
                  </Button>
                </>
              )}
            </p>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
}
